import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useNavigate } from 'react-router-dom';

interface FirstSessionWelcomeModalProps {
  open: boolean;
  onClose: () => void;
}

interface InfoItem {
  icon: string;
  title: string;
  body: string;
}

const INFO_ITEMS: InfoItem[] = [
  {
    icon: '🔁',
    title: '¿Qué son los horarios de reprogramación?',
    body: 'El sistema reserva automáticamente tus primeras 4 horas disponibles para que tus pacientes activos puedan reagendar. Recién a partir de la 5ta hora disponible en adelante, los horarios se ofrecen a nuevos pacientes. Por eso el mínimo importa: sin esas 4 horas cubiertas, tus pacientes activos quedan sin opciones para reagendar.',
  },
  {
    icon: '⏱️',
    title: '¿Cuántos necesitás y cómo se cuentan?',
    body: 'Al menos 4 horas en los próximos 7 días corridos (desde mañana). Cuentan todos tus horarios disponibles —Estándar y Reprogramación—. El sistema toma las primeras 4h para reprogramación; el resto se libera para nuevos pacientes.',
  },
  {
    icon: '⚙️',
    title: '¿Cómo configurarlos?',
    body: 'En tu Agenda → "Configurar horarios" → Horario fijo o Día específico. Usá el tag "Repr." para marcar explícitamente qué horarios querés proteger para reprogramación y que no sean tomados por nuevos pacientes. Con "Est." también cuentan para el mínimo, pero el sistema elige cuáles usar.',
  },
];

export const FIRST_SESSION_WELCOME_KEY = 'psi-first-session-welcome-seen';

export const FirstSessionWelcomeModal: React.FC<FirstSessionWelcomeModalProps> = ({
  open,
  onClose,
}) => {
  const navigate = useNavigate();

  const handleGoToCalendar = () => {
    onClose();
    navigate('/calendar');
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        data-testid="first-session-welcome-modal"
        className="sm:max-w-[32rem] p-0 overflow-hidden"
      >
        {/* ── Header ── */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-100">
          <DialogTitle className="text-[1.125rem] font-bold text-[#4a3d73] leading-snug">
            ¡Completaste tu primera sesión! 🎉
          </DialogTitle>
          <p className="text-[0.875rem] text-gray-500 mt-1.5 leading-snug">
            Ahora que ya tenés tu primer paciente activo, hay algo importante que configurar.
          </p>
        </DialogHeader>

        {/* ── Body ── */}
        <div className="px-6 py-5 space-y-5 overflow-y-auto max-h-[55vh]">
          {INFO_ITEMS.map((item) => (
            <InfoCard key={item.title} item={item} />
          ))}
        </div>

        {/* ── Footer ── */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-between items-center gap-3 flex-wrap">
          <button
            type="button"
            data-testid="first-session-welcome-later-btn"
            onClick={onClose}
            className="text-sm text-gray-500 hover:text-gray-700 bg-transparent border-none cursor-pointer transition-colors"
          >
            Lo hago después
          </button>
          <button
            type="button"
            data-testid="first-session-welcome-go-btn"
            onClick={handleGoToCalendar}
            className="px-5 py-2 rounded-full text-sm font-semibold text-white border-none cursor-pointer transition-all hover:brightness-90"
            style={{ background: '#6D57BC' }}
          >
            Ir a configurar mi agenda
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const InfoCard: React.FC<{ item: InfoItem }> = ({ item }) => (
  <div className="flex gap-3 items-start">
    <span className="text-lg leading-none mt-0.5 shrink-0" aria-hidden>
      {item.icon}
    </span>
    <div className="min-w-0">
      <p className="text-[0.8125rem] font-semibold text-[#4a3d73] leading-snug mb-1">
        {item.title}
      </p>
      <p className="text-[0.8125rem] text-gray-600 leading-relaxed">
        {item.body}
      </p>
    </div>
  </div>
);

export default FirstSessionWelcomeModal;
